import React, { useState, useEffect, useContext } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Leaf,
  MapPin,
  Users,
  FileText,
  Scale,
  DollarSign,
  Layers,
  ClipboardList,
} from 'lucide-react';
import { MapContext } from '../../contexts/MapContext';
import * as PropertyService from '../../services/PropertyService';
import { extractCarCodes, extractSigefCodes } from '../../utils/propertyUtils/domainCodeExtractor';
import { propertyApi } from '../../services/BaseApiService.jsx';
import { usePropertyProcessingStream } from '../../hooks/usePropertyProcessingStream';

// Importação dos componentes de domínio específicos
import CarTab from './CarTab';
import SigefTab from './SigefTab';
import SncrTab from './SncrTab';
import CafirTab from './CafirTab';
// Removidos componentes não utilizados: CarDashboardTab, GeospatialAnalysisTab
import EntidadesTab from './EntidadesTab';
import LegalTab from './LegalTab';
import TemasTab from './TemasTab';
import FaturamentoTab from './FaturamentoTab';
import CertidoesTab from './CertidoesTab';
import CcirTab from './CcirTab';
import CndTab from './CndTab';

/**
 * Componente de navegação por abas para os diferentes domínios da propriedade rural
 * (CAR, SIGEF, SNCR, CAFIR) com layout padronizado e indicação visual de disponibilidade.
 * Consome diretamente os dados JSON do backend.
 *
 * @param {Object} propertyData - Dados da propriedade do backend
 * @param {Function} onHighlightGeometry - Função para destacar geometria no mapa
 * @param {Function} onTabChange - Função callback chamada quando a tab muda (opcional)
 */
const PropertyTabs = ({ propertyData, onHighlightGeometry = () => {}, onTabChange = null }) => {
  const { setPropertyData, setLoading } = useContext(MapContext);
  // Não retorne antes dos Hooks; trate ausência de dados no render condicional mais abaixo

  // Função para determinar quais domínios estão disponíveis baseado na estrutura do JSON
  const detectAvailableDomains = () => {
    const domains = [];

    if (!propertyData || !propertyData.properties) return domains;

    const props = propertyData.properties;
    const getKeyCI = (obj, target) =>
      Object.keys(obj || {}).find((k) => k.toLowerCase() === target);
    const hasAnyData = (v) => {
      if (!v) return false;
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === 'object') return Object.keys(v).length > 0;
      return !!v;
    };
    // ENTIDADES (case-insensitive)
    const entidadesKey = getKeyCI(props, 'entidades');
    if (entidadesKey && hasAnyData(props[entidadesKey])) domains.push('ENTIDADES');
    // TEMAS/SOBREPOSIÇÃO (case-insensitive)
    const temasKey = getKeyCI(props, 'temas') || getKeyCI(props, 'sobreposicao');
    if (temasKey && hasAnyData(props[temasKey])) domains.push('TEMAS');

    // Log para depuração da estrutura de dados
    //console.log('[PropertyTabs] Verificando domínios disponíveis');
    //console.log('[PropertyTabs] Estrutura de dados:', {
    //  properties: props,
    //  car: props.car,
    //  sigef: props.sigef,
    //  sncr: props.sncr,
    //  cafir: props.cafir
    //});

    // Verificar CAR (case-insensitive, array ou objeto com códigos)
    const carKey = getKeyCI(props, 'car');
    if (carKey) {
      const carVal = props[carKey];
      if (
        (Array.isArray(carVal) && carVal.length > 0) ||
        (carVal &&
          typeof carVal === 'object' &&
          (carVal.cod_imovel || carVal.codigo_imovel_incra || carVal.codigo)) ||
        hasAnyData(carVal)
      ) {
        domains.push('CAR');
      }
    }

    // Verificar SIGEF (case-insensitive)
    const sigefKey = getKeyCI(props, 'sigef');
    if (sigefKey) {
      const v = props[sigefKey];
      if (hasAnyData(v)) domains.push('SIGEF');
    }

    // Verificar SNCR (case-insensitive)
    const sncrKey = getKeyCI(props, 'sncr');
    if (sncrKey) {
      const v = props[sncrKey];
      const hasTit = Array.isArray(v?.titulares) && v.titulares.length > 0;
      const hasCode = !!(
        v &&
        typeof v === 'object' &&
        (v.codigo_imo || v.codigo || v.codigo_imovel_incra)
      );
      if (hasTit || hasCode || hasAnyData(v)) domains.push('SNCR');
    }

    // Verificar CAFIR (case-insensitive)
    const cafirKey = getKeyCI(props, 'cafir');
    if (cafirKey) {
      const v = props[cafirKey];
      if (hasAnyData(v)) domains.push('CAFIR');
    }

    // Verificar CCIR (SERPRO ccir)
    const ccirKey = getKeyCI(props, 'serpro_ccir') || getKeyCI(props, 'ccir');
    if (ccirKey) {
      const v = props[ccirKey];
      if (hasAnyData(v)) domains.push('CCIR');
    }

    // Verificar CND (SERPRO cnd)
    const cndKey = getKeyCI(props, 'serpro_cnd') || getKeyCI(props, 'cnd');
    if (cndKey) {
      const v = props[cndKey];
      if (hasAnyData(v)) domains.push('CND');
    }

    // Incluir LEGAL somente quando houver dados (case-insensitive)
    const legalKey = getKeyCI(props, 'legal');
    if (legalKey && hasAnyData(props[legalKey])) domains.push('LEGAL');
    // Incluir FATURAMENTO somente quando houver dados (case-insensitive)
    const faturKey = getKeyCI(props, 'faturamento');
    if (faturKey && hasAnyData(props[faturKey])) domains.push('FATURAMENTO');
    // CERTIDOES será controlado por verificação de PDFs no AFS via backend (/docs/{file_id})

    return domains;
  };

  // Estado para armazenar os domínios disponíveis (UI-ready)
  const [availableDomains, setAvailableDomains] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [docsAvailable, setDocsAvailable] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false); // overlay de processamento até process_succeeded (SSE consolidate + fallback polling)

  // Cache local por domínio para dados carregados sob demanda do backend (state:{file_id})
  const [domainCache, setDomainCache] = useState({});
  // Aba ativa da UI (declarada cedo para ser usada em efeitos posteriores)
  const [activeTab, setActiveTab] = useState('');

  // Normaliza fileId para uso nos endpoints de documentos
  const rawFileId =
    propertyData?._metadata?.file_id ||
    propertyData?._contextData?.filename ||
    propertyData?._contextData?.accepted_file_id ||
    '';
  const fileId =
    typeof rawFileId === 'string' && rawFileId.toLowerCase().endsWith('.json')
      ? rawFileId.slice(0, -5)
      : rawFileId;

  // Usa os mesmos extratores do cabeçalho para inferir "Não registrada" vs. dados presentes
  // Observação: a verificação de dados por domínio é feita adiante via hasDomainData(domainUpper),
  // que considera tanto o cache local quanto propertyData.properties

  // Garante que o cache local seja limpo quando mudamos de propriedade (novo file_id)
  useEffect(() => {
    setDomainCache({});
  }, [fileId]);

  // Verifica disponibilidade de PDFs de certidões no backend
  useEffect(() => {
    let cancelled = false;
    async function fetchDocs() {
      try {
        if (!fileId || typeof fileId !== 'string') {
          setDocsAvailable(false);
          return;
        }
        const res = await propertyApi.get(`/api/v1/docs/${encodeURIComponent(fileId)}`);
        const count = res?.data?.count ?? 0;
        if (!cancelled) setDocsAvailable(count > 0);
      } catch (_e) {
        if (!cancelled) setDocsAvailable(false);
      }
    }
    fetchDocs();
    return () => {
      cancelled = true;
    };
  }, [fileId]);

  // Cleanup ao trocar de propriedade (o hook de stream abaixo faz o cleanup do SSE/polling).
  useEffect(() => {
    setIsProcessing(false);
  }, [fileId]);

  // Acompanhamento do processamento via SSE (consolidate-service) com
  // fallback automático para polling em /api/v1/process/{fid} se o
  // EventSource falhar (DP-4a). O hook só dispara quando isProcessing.
  const {
    terminal: processingTerminal,
    source: processingSource,
    error: processingError,
  } = usePropertyProcessingStream(fileId, { enabled: isProcessing });

  // Hidrata snapshot final quando o processamento terminar.
  useEffect(() => {
    if (!isProcessing || !processingTerminal || !fileId) return;
    let cancelled = false;
    (async () => {
      try {
        const jsonRes = await propertyApi.get(`/api/v1/state/${encodeURIComponent(fileId)}`);
        if (cancelled) return;
        const consolidated = jsonRes?.data;
        const propertyDataFinal = PropertyService.processarDadosPropriedade(consolidated);
        setPropertyData(propertyDataFinal);
        setDomainCache({});
        try {
          const resDocs = await propertyApi.get(`/api/v1/docs/${encodeURIComponent(fileId)}`);
          if (!cancelled) setDocsAvailable((resDocs?.data?.count ?? 0) > 0);
        } catch (_) {
          /* mantém estado atual em caso de falha de docs */
        }
      } catch (err) {
        console.warn('[PropertyTabs] falha ao hidratar snapshot final:', err);
      } finally {
        if (!cancelled) setIsProcessing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // setPropertyData não precisa ser dep (vem de contexto estável).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProcessing, processingTerminal, fileId]);

  // Debug opcional do caminho de acompanhamento (sse | polling).
  useEffect(() => {
    if (!isProcessing) return;
    const debug = import.meta.env.VITE_DEBUG === 'true';
    if (debug && processingSource) {
      // eslint-disable-next-line no-console
      console.log('[PropertyTabs] processing source =', processingSource);
    }
    if (processingError) {
      console.warn('[PropertyTabs] processing stream error:', processingError);
    }
  }, [isProcessing, processingSource, processingError]);

  // Atualiza os domínios disponíveis sempre que propertyData ou docsAvailable mudar
  useEffect(() => {
    const legacy = detectAvailableDomains();
    const withDocs = docsAvailable ? Array.from(new Set([...(legacy || []), 'CERTIDOES'])) : legacy;
    const hasTemasArray = Array.isArray(propertyData?.properties?.sobreposicao)
      ? propertyData.properties.sobreposicao.length > 0
      : Array.isArray(propertyData?.properties?.temas) && propertyData.properties.temas.length > 0;
    const final = hasTemasArray ? Array.from(new Set([...(withDocs || []), 'TEMAS'])) : withDocs;
    setAvailableDomains(final);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyData, docsAvailable]);

  // Botão 'Atualizar dados' depende apenas do clique local (isUpdating)
  useEffect(() => {
    /* no-op: mantemos para consistência futura */
  }, []);

  // Helper local para normalizar domínio em lower conforme mapeamento das tabs (mapLower mais abaixo)

  // Sem fetch incremental por domínio: aguardamos ciclo final e/ou botão de atualizar
  useEffect(() => {
    // No-op: mantemos para possível futura lógica leve
  }, [fileId, activeTab, propertyData, domainCache]);

  //console.log('[PropertyTabs] Domínios disponíveis:', availableDomains);

  // Dicionário com configurações de exibição para cada domínio
  const domainConfig = {
    CAR: {
      icon: <Leaf size={16} />,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      label: 'Cadastro Ambiental Rural',
    },
    SIGEF: {
      icon: <MapPin size={16} />,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      label: 'Sistema de Gestão Fundiária',
    },
    SNCR: {
      icon: <Users size={16} />,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      label: 'Sistema Nacional de Cadastro Rural',
    },
    CAFIR: {
      icon: <FileText size={16} />,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      label: 'Cadastro de Imóveis Rurais',
    },
    ENTIDADES: {
      icon: <Users size={16} />,
      color: 'text-amber-700',
      bgColor: 'bg-amber-50',
      label: 'Entidades',
    },
    LEGAL: {
      icon: <Scale size={16} />,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      label: 'Informações Jurídicas',
    },
    FATURAMENTO: {
      icon: <DollarSign size={16} />,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      label: 'Faturamento',
    },
    CCIR: {
      icon: <ClipboardList size={16} />,
      color: 'text-teal-600',
      bgColor: 'bg-teal-50',
      label: 'Cadastro de Imóveis Rurais',
    },
    CND: {
      icon: <FileText size={16} />,
      color: 'text-violet-700',
      bgColor: 'bg-violet-50',
      label: 'Certidão Negativa de Débitos',
    },
    TEMAS: {
      icon: <Layers size={16} />,
      color: 'text-slate-700',
      bgColor: 'bg-slate-50',
      label: 'Temas Ambientais',
    },
  };

  // Tabs são 100% orientadas a dados (data-driven)

  // Determina a aba ativa inicialmente com base na prioridade
  const getInitialActiveTab = () => {
    if (availableDomains.length === 0) return '';
    const priorityOrder = [
      'CAR',
      'SIGEF',
      'SNCR',
      'CAFIR',
      'CND',
      'ENTIDADES',
      'LEGAL',
      'TEMAS',
      'FATURAMENTO',
      'CCIR',
      'CERTIDOES',
    ];
    for (const domain of priorityOrder) {
      if (availableDomains.includes(domain)) {
        return domain;
      }
    }
    return availableDomains[0];
  };
  // Inicializa com um valor válido, garantindo que seja sempre uma tab disponível
  // Mantém activeTab sempre válido quando availableDomains muda
  useEffect(() => {
    const initialTab = getInitialActiveTab();
    if (!activeTab) {
      if (initialTab) setActiveTab(initialTab);
    } else if (!availableDomains.includes(activeTab)) {
      if (initialTab) setActiveTab(initialTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableDomains]);
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (typeof onTabChange === 'function') {
      onTabChange(newValue.toLowerCase());
    }
  };
  // Hooks adicionais (useRef/useEffect) devem vir antes de qualquer early return
  const openDomainTabRef = React.useRef();
  openDomainTabRef.current = (domain) => {
    const normalizedDomain = domain.toUpperCase();
    if (availableDomains.includes(normalizedDomain)) {
      setActiveTab(normalizedDomain);
      return true;
    } else {
      return false;
    }
  };
  const openDomainTab = (domain) => {
    return openDomainTabRef.current(domain);
  };
  useEffect(() => {
    if (typeof onTabChange === 'function') {
      const DEBUG = import.meta.env.VITE_DEBUG === 'true';
      if (DEBUG) onTabChange.openDomainTab = openDomainTab;
    }
  }, [availableDomains, onTabChange]);

  // Se não houver domínios disponíveis, não exibe nada
  if (availableDomains.length === 0) {
    return (
      <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
        <div className="text-gray-500 text-center py-6">
          Não há dados disponíveis para esta propriedade
        </div>
      </div>
    );
  }

  // Evita renderizar Tabs com value inválido em um primeiro ciclo
  if (!activeTab) {
    return null;
  }

  // Conjunto de tabs a desenhar: união entre os disponíveis e os esperados, para aparecerem desabilitadas até ficarem ready
  const mapLower = (d) =>
    ({
      CAR: 'car',
      SIGEF: 'sigef',
      SNCR: 'sncr',
      CAFIR: 'cafir',
      CND: 'serpro_cnd',
      CERTIDOES: 'certidoes',
      ENTIDADES: 'entidades',
      LEGAL: 'legal',
      TEMAS: 'temas',
      FATURAMENTO: 'faturamento',
      CCIR: 'serpro_ccir',
    })[d] || d.toLowerCase();
  const tabsToRender = Array.from(new Set([...(availableDomains || [])]));

  // Funções auxiliares: apenas verificação de dados disponível
  const _isDomainReady = (domainUpper) => availableDomains.includes(domainUpper);
  const hasDomainData = (domainUpper) => {
    const lower = mapLower(domainUpper);
    // cache local vence
    if (domainCache[lower]) return true;
    let pdata = propertyData?.properties?.[lower] || propertyData?.properties?.[domainUpper];
    if (lower === 'temas' && !pdata) {
      // Backward/forward compat: aceitar 'sobreposicao' como fonte de dados
      pdata = propertyData?.properties?.['sobreposicao'];
    }
    if (!pdata) return false;

    const nonEmpty = (v) => {
      if (!v) return false;
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === 'object') return Object.keys(v).length > 0;
      return !!v;
    };

    switch (lower) {
      case 'car': {
        if (Array.isArray(pdata) && pdata.length > 0) return true;
        if (typeof pdata === 'object') {
          return !!(
            pdata.cod_imovel ||
            pdata.codigo_imovel_incra ||
            pdata.codigo ||
            nonEmpty(pdata.themes)
          );
        }
        return false;
      }
      case 'sigef': {
        const hasCode = (obj) => !!(obj?.codigo_imo || obj?.codigo);
        const hasParcels = (obj) => Array.isArray(obj?.parcels) && obj.parcels.length > 0;
        if (Array.isArray(pdata)) {
          return pdata.some((it) => hasCode(it) || hasParcels(it));
        }
        if (typeof pdata === 'object') {
          return hasCode(pdata) || hasParcels(pdata) || nonEmpty(pdata);
        }
        return false;
      }
      case 'sncr': {
        const hasTit = (obj) => Array.isArray(obj?.titulares) && obj.titulares.length > 0;
        const hasCode = (obj) =>
          !!(obj?.codigo_imo || obj?.codigo || obj?.codigo_imovel_incra || obj?.codigo_incra);
        const hasPeople = (obj) =>
          typeof obj?.total_pessoas_relacionadas_imovel === 'number' &&
          obj.total_pessoas_relacionadas_imovel > 0;
        if (Array.isArray(pdata)) {
          return pdata.some((it) => hasTit(it) || hasCode(it) || hasPeople(it));
        }
        if (typeof pdata === 'object') {
          return hasTit(pdata) || hasCode(pdata) || hasPeople(pdata) || nonEmpty(pdata);
        }
        return false;
      }
      case 'cafir': {
        const nirfFields = ['nirf', 'NIRF', 'codigo_nirf', 'CODIGO_NIRF'];
        const hasNirf = (obj) => {
          if (!obj || typeof obj !== 'object') return false;
          for (const f of nirfFields) {
            const v = obj?.[f];
            if (typeof v === 'string' && v.trim()) return true;
          }
          if (Array.isArray(obj?.details)) {
            for (const d of obj.details) {
              for (const f of nirfFields) {
                const v = d?.[f];
                if (typeof v === 'string' && v.trim()) return true;
              }
            }
          }
          return false;
        };
        if (Array.isArray(pdata)) return pdata.some((it) => hasNirf(it) || nonEmpty(it));
        if (typeof pdata === 'object') return hasNirf(pdata) || nonEmpty(pdata);
        return false;
      }
      default:
        return nonEmpty(pdata);
    }
  };

  // Sem status/chips em tempo real

  // Retorna uma cópia do propertyData enriquecida com o cache do domínio, quando existir
  const getEffectivePropertyData = (domainUpper) => {
    const lower = mapLower(domainUpper);
    const cached = domainCache[lower];
    if (!cached) return propertyData;
    return {
      ...(propertyData || {}),
      properties: {
        ...(propertyData?.properties || {}),
        [lower]: cached,
      },
    };
  };

  // Skeleton removido (sem fases em tempo real)

  return (
    <div className="bg-white rounded-lg shadow-sm mb-4" style={{ position: 'relative' }}>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="property domains tabs"
        >
          {/* Primeira parte: CAR, SIGEF, SNCR, CAFIR */}
          {['CAR', 'SIGEF', 'SNCR', 'CAFIR'].map((domain) => {
            if (!tabsToRender.includes(domain)) return null;
            // Abas só aparecem quando há dados (tabsToRender); não desabilitar
            const disabled = false;

            const config = domainConfig[domain] || {
              icon: <FileText size={16} />,
              color: 'text-gray-600',
              label: domain,
            };

            return (
              <Tab
                key={domain}
                label={
                  <div className="flex items-center">
                    <span className={`mr-1.5 ${config.color}`}>{config.icon}</span>
                    <span>{domain}</span>
                  </div>
                }
                value={domain}
                aria-label={`Tab ${config.label}`}
                disabled={disabled}
              />
            );
          })}

          {/* Segunda parte: CND, ENTIDADES, LEGAL, FATURAMENTO, CERTIDOES */}
          {(() => {
            const secondGroup = [
              'CND',
              'ENTIDADES',
              'LEGAL',
              'TEMAS',
              'FATURAMENTO',
              'CCIR',
              'CERTIDOES',
            ];
            const firstSecondGroupDomain = secondGroup.find((d) => tabsToRender.includes(d));
            return secondGroup.map((domain) => {
              if (!tabsToRender.includes(domain)) return null;
              // Para secundárias, tab visível implica dados presentes (ou PDFs no caso de CERTIDOES)
              const disabled = false;

              const config = domainConfig[domain] || {
                icon: <FileText size={16} />,
                color: 'text-gray-600',
                label: domain,
              };

              return (
                <Tab
                  key={domain}
                  label={
                    <div className="flex items-center">
                      <span className={`mr-1.5 ${config.color}`}>{config.icon}</span>
                      <span>{domain === 'TEMAS' ? 'Sobreposições' : domain}</span>
                    </div>
                  }
                  value={domain}
                  aria-label={`Tab ${config.label}`}
                  sx={firstSecondGroupDomain === domain ? { ml: 2 } : undefined}
                  disabled={disabled}
                />
              );
            });
          })()}
        </Tabs>
        {/* Botão de Atualizar Dados (force_refresh) */}
        <Box sx={{ pr: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={isUpdating}
            onClick={async () => {
              try {
                setIsUpdating(true);
                setIsProcessing(true);
                const hints = propertyData?._contextData?.hints || {};
                // Monta params a partir dos hints ou extração dos dados atuais
                const params = {};
                if (hints.cod_imovel) params.cod_imovel = hints.cod_imovel;
                if (hints.codigo_imo) params.codigo_imo = hints.codigo_imo;
                if (!params.cod_imovel) {
                  const cars = extractCarCodes(propertyData);
                  if (cars && cars.length > 0) params.cod_imovel = cars[0];
                }
                if (!params.codigo_imo) {
                  const sigefs = extractSigefCodes(propertyData);
                  if (sigefs && sigefs.length > 0) params.codigo_imo = sigefs[0];
                }
                // Requer ao menos um identificador
                if (!params.cod_imovel && !params.codigo_imo) {
                  console.warn('[PropertyTabs] Não há códigos suficientes para atualizar.');
                  return;
                }
                params.force_refresh = true;
                if (typeof setLoading === 'function') setLoading(true);
                try {
                  await PropertyService.buscarPorCodigo(params, setPropertyData, setLoading);
                  // IMPORTANTE: limpar cache local para não sobrescrever o snapshot novo
                  setDomainCache({});
                  // Revalidar documentos disponíveis (CERTIDÕES) sem depender de mudança de fileId
                  try {
                    if (fileId && typeof fileId === 'string') {
                      const res = await propertyApi.get(
                        `/api/v1/docs/${encodeURIComponent(fileId)}`,
                      );
                      const count = res?.data?.count ?? 0;
                      setDocsAvailable(count > 0);
                    }
                  } catch (_) {
                    /* mantém estado atual se falhar */
                  }

                  // Acompanhamento do progresso agora é feito pelo hook
                  // `usePropertyProcessingStream(fileId, { enabled: isProcessing })`
                  // no topo do componente: SSE via consolidate-service com
                  // fallback automático para polling. O efeito de terminação
                  // hidrata o snapshot final (state + docs). Nada a fazer aqui.
                } finally {
                  setIsUpdating(false);
                }
              } catch (err) {
                console.warn('[PropertyTabs] Atualizar dados falhou:', err);
                setIsUpdating(false);
                setIsProcessing(false);
              }
            }}
          >
            {isUpdating ? (
              <span className="inline-flex items-center">
                <CircularProgress size={14} sx={{ mr: 1 }} />
                Atualizando...
              </span>
            ) : (
              'Atualizar dados'
            )}
          </Button>
        </Box>
      </Box>
      {isProcessing && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(255,255,255,0.6)',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={22} />
            <span>Processando atualização...</span>
          </Box>
        </Box>
      )}

      {/* Conteúdo da aba selecionada */}
      <Box p={3}>
        {activeTab === 'CAR' &&
          (hasDomainData('CAR') ? (
            <CarTab
              propertyData={getEffectivePropertyData('CAR')}
              onHighlightGeometry={onHighlightGeometry}
            />
          ) : (
            <div className="text-gray-500">
              Nenhum dado do CAR disponível para esta propriedade.
            </div>
          ))}
        {/* CarDashboardTab removido daqui - agora será exibido em modal */}
        {activeTab === 'SIGEF' &&
          (hasDomainData('SIGEF') ? (
            <SigefTab
              propertyData={getEffectivePropertyData('SIGEF')}
              onHighlightGeometry={onHighlightGeometry}
            />
          ) : (
            <div className="text-gray-500">
              Nenhum dado do SIGEF disponível para esta propriedade.
            </div>
          ))}
        {activeTab === 'SNCR' &&
          (hasDomainData('SNCR') ? (
            <SncrTab
              propertyData={getEffectivePropertyData('SNCR')}
              onHighlightGeometry={onHighlightGeometry}
            />
          ) : (
            <div className="text-gray-500">
              Nenhum dado do SNCR disponível para esta propriedade.
            </div>
          ))}
        {activeTab === 'CAFIR' &&
          (hasDomainData('CAFIR') ? (
            <CafirTab
              propertyData={getEffectivePropertyData('CAFIR')}
              onHighlightGeometry={onHighlightGeometry}
            />
          ) : (
            <div className="text-gray-500">
              Nenhum dado do CAFIR disponível para esta propriedade.
            </div>
          ))}
        {activeTab === 'ENTIDADES' &&
          (hasDomainData('ENTIDADES') ? (
            <EntidadesTab propertyData={getEffectivePropertyData('ENTIDADES')} />
          ) : (
            <div className="text-gray-500">
              Nenhum dado de Entidades disponível para esta propriedade.
            </div>
          ))}
        {activeTab === 'LEGAL' &&
          (hasDomainData('LEGAL') ? (
            <LegalTab
              propertyData={getEffectivePropertyData('LEGAL')}
              onHighlightGeometry={onHighlightGeometry}
            />
          ) : (
            <div className="text-gray-500">Nenhum dado Legal disponível para esta propriedade.</div>
          ))}
        {activeTab === 'TEMAS' &&
          (hasDomainData('TEMAS') ? (
            <TemasTab propertyData={getEffectivePropertyData('TEMAS')} />
          ) : (
            <div className="text-gray-500">
              Nenhum dado de Temas disponível para esta propriedade.
            </div>
          ))}
        {activeTab === 'FATURAMENTO' &&
          (hasDomainData('FATURAMENTO') ? (
            <FaturamentoTab
              propertyData={getEffectivePropertyData('FATURAMENTO')}
              onHighlightGeometry={onHighlightGeometry}
            />
          ) : (
            <div className="text-gray-500">
              Nenhum dado de Faturamento disponível para esta propriedade.
            </div>
          ))}
        {activeTab === 'CCIR' &&
          (hasDomainData('CCIR') ? (
            <CcirTab propertyData={getEffectivePropertyData('CCIR')} />
          ) : (
            <div className="text-gray-500">
              Nenhum dado do CCIR disponível para esta propriedade.
            </div>
          ))}
        {activeTab === 'CND' &&
          (hasDomainData('CND') ? (
            <CndTab propertyData={getEffectivePropertyData('CND')} />
          ) : (
            <div className="text-gray-500">
              Nenhum dado do CND disponível para esta propriedade.
            </div>
          ))}
        {activeTab === 'CERTIDOES' &&
          (docsAvailable ? (
            <CertidoesTab
              propertyData={getEffectivePropertyData('CERTIDOES')}
              onHighlightGeometry={onHighlightGeometry}
            />
          ) : (
            <div className="text-gray-500">Nenhum documento disponível para esta propriedade.</div>
          ))}
      </Box>
    </div>
  );
};

export default PropertyTabs;
